import { z } from 'zod';
import type { AdjustmentCategory, AdjustmentTargetType } from '../types';

export const TARGET_TYPES: [AdjustmentTargetType, ...AdjustmentTargetType[]] = [
  'PURCHASE',
  'PAYMENT',
  'REFUND',
  'SETTLEMENT',
];

export const CATEGORIES: [AdjustmentCategory, ...AdjustmentCategory[]] = [
  'RECONCILIATION_CORRECTION',
  'GOODWILL',
  'CHARGEBACK',
  'WRITE_OFF',
  'OTHER',
];

/** Converts a string in Rupees (e.g. "-200.50") into integer paise (-20050) without float errors. */
export function rupeesStringToPaise(val: string): number {
  const trimmed = val.trim();
  if (!trimmed) return 0;
  const num = Number(trimmed);
  if (Number.isNaN(num)) return 0;
  return Math.round(num * 100);
}

/** Formats paise (signed) to Indian Rupee standard format (e.g. ₹1,499.00 or -₹200.00). */
export function formatPaiseToRupees(paise: number | null | undefined): string {
  if (paise === null || paise === undefined) return '—';
  const isNegative = paise < 0;
  const absoluteRupees = (Math.abs(paise) / 100).toFixed(2);
  const formatted = new Intl.NumberFormat('en-IN', {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(Number(absoluteRupees));

  return isNegative ? `-₹${formatted}` : `₹${formatted}`;
}

export const resolveFindingFormSchema = z
  .object({
    resolution: z
      .string()
      .trim()
      .min(3, 'Resolution explanation must be at least 3 characters')
      .max(1000, 'Resolution explanation must be 1000 characters or fewer'),
    createAdjustment: z.boolean().default(false),
    targetType: z.enum(TARGET_TYPES).default('PAYMENT'),
    targetId: z.string().trim().default(''),
    category: z.enum(CATEGORIES).default('RECONCILIATION_CORRECTION'),
    amountRupees: z.string().default('0'),
  })
  .superRefine((data, ctx) => {
    if (data.createAdjustment) {
      if (!data.targetId || data.targetId.trim().length === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['targetId'],
          message: 'Target ID is required when creating a financial adjustment',
        });
      }
      const num = Number(data.amountRupees);
      if (Number.isNaN(num) || num === 0) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          path: ['amountRupees'],
          message: 'Adjustment amount must be a non-zero number (positive for credit, negative for debit)',
        });
      }
    }
  });

export type ResolveFindingFormValues = z.infer<typeof resolveFindingFormSchema>;
